<script setup lang="ts">
import debounce from 'lodash/debounce'
import { DaneCode } from '~/lib/models/lupap'
import { ToastType } from '~/lib/models/ui/toast'
import { Address } from '~/lib/services/store/order/order.dto'
import { lupapService } from '~/lib/services/store/restaurant/lupap/lupap.service'

const emit = defineEmits(['close'])

const appStore = useAppStore()
const restaurantStore = useRestaurantStore()
const { t } = useLocale()
const { trackGeolocate } = useGTM()
const { $toast } = useNuxtApp()
const { daneCodes } = useLupap()

const city = ref<DaneCode>()
const geolocatedStreet = ref()
const lastStep = ref(false)
const selectedLocation = ref()
const buttonBottom = ref(24)

const lupapWebToken = computed(() => appStore.ecommerceConfig?.lupapWebToken ?? '')

const onClose = () => {
  if (city.value) {
    city.value = undefined
  } else emit('close')
}

const setCity = ($ev: DaneCode) => {
  geolocatedStreet.value = undefined
  city.value = $ev
}

const onContinue = (address: Address) => {
  if (!city.value) return null
  selectedLocation.value = {
    description: `${address.address}, ${city.value.city}`,
    latitude: address.latitude,
    longitude: address.longitude,
  }
  // go to pin adjust map with lupap address
  restaurantStore.setAddress(address)
  lastStep.value = true
}

const previousStep = () => {
  lastStep.value = false
}

const localizeMe = async () => {
  trackGeolocate()
  // permission status
  const permissionStatus = await getPermissionStatus('geolocation')
  if (permissionStatus === 'denied' || permissionStatus === null) {
    const isDenied = permissionStatus === 'denied'
    const toastConfig = {
      type: isDenied ? ToastType.WARNING : ToastType.ERROR,
      content: t(`errors.toast.geo.${isDenied ? 'denied' : 'unsupported'}`),
    }
    $toast.show(toastConfig)
    return null
  }

  try {
    // coords
    const userLocation = await getUserLocation()
    if (!userLocation) return null
    // fetch lupap reverse
    const {
      properties: { city: cityKey, address: street },
    } = await lupapService.getAddressFromCoords({
      webToken: lupapWebToken.value,
      coords: userLocation,
    })
    // set city from lupap-dane-codes and set street
    const daneCode = daneCodes.find(({ key }) => key === cityKey)
    if (!daneCode) throw new Error('Not mapped daneCode: ' + cityKey)
    geolocatedStreet.value = street
    city.value = daneCode
  } catch (err) {
    // show toast error generic
    $toast.show({
      type: ToastType.ERROR,
      content: t('errors.toast.generic'),
    })
  }
}

const viewportHandler = () => {
  const viewport = window.visualViewport
  if (viewport) {
    const offsetTop =
      viewport.height - document.body.getBoundingClientRect().height + viewport.offsetTop
    buttonBottom.value = 24 + Math.abs(offsetTop)
  }
}

const viewportHandlerDebounced = debounce(viewportHandler, 50)

onMounted(() => {
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', viewportHandlerDebounced)
    window.visualViewport.addEventListener('scroll', viewportHandlerDebounced)
  }
})

onBeforeUnmount(() => {
  if (window.visualViewport) {
    window.removeEventListener('resize', viewportHandlerDebounced, false)
    window.removeEventListener('scroll', viewportHandlerDebounced, false)
  }
})
</script>

<template>
  <ModalCenter :add-close-button="false" :content-full="true">
    <template #header>
      <div class="flex min-h-[2.5rem] w-full flex-row items-center gap-4">
        <Chevron fill="#292929" class="h-6 w-6 -rotate-180 cursor-pointer" @click="onClose" />
        <p v-if="city" class="text-lg">{{ t('selectRestaurant.DLV.confirmAddress') }}</p>
      </div>
    </template>
    <template #content>
      <section class="flex-1 p-4 md:mx-auto md:w-[80%] md:p-0">
        <template v-if="!city">
          <LupapCityAutoComplete @set-city="setCity" />
          <!-- LocateMe floating button -->
          <div
            class="absolute left-0 right-0 z-[11] px-4 transition-[bottom]"
            :style="{ bottom: `${buttonBottom}px` }"
          >
            <McdButton
              is-primary
              class="w-full py-3 text-sm md:mx-auto md:w-fit md:px-6"
              @on-click="localizeMe"
            >
              <div class="flex flex-row items-center justify-center gap-2">
                <img src="/images/icons/locateMe.svg" alt="locateMe" />
                <span class="block">{{ t('home.searchLocateMe') }}</span>
              </div>
            </McdButton>
          </div>
        </template>
        <LazyLupapAddress
          v-else
          :city-dane="city"
          :street="geolocatedStreet"
          :lupap-token="lupapWebToken"
          @continue="onContinue"
        />
      </section>
    </template>
  </ModalCenter>
  <LazyDLVSelectRestaurant
    v-if="lastStep"
    :state="{ action: 'SELECT_ADDRESS', address: undefined }"
    :selected-location="selectedLocation"
    :previous-page="previousStep"
    @address-selected="onClose"
  />
</template>
